<template>
    <!-- ✅ Ensure modal remains in the DOM with v-show -->
    <div
        class="modal fade"
        :id="modalId"
        data-bs-backdrop="false"
        tabindex="-1"
        aria-labelledby="modalLabel"
        aria-hidden="true"
        v-show="modalShow"
    >
        <div
            class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            :class="`modal-${modalSize}`"
        >
            <div class="modal-content">
                <div
                    class="modal-header justify-content-between align-items-center"
                >
                    <!-- Left: Custom icon (e.g., share) -->
                    <div class="modal-icon-left">
                        <slot name="left-icon">
                            <!-- Default: Empty or fallback icon -->
                        </slot>
                    </div>

                    <!-- Center: Modal title -->
                    <h5 class="modal-title text-center flex-grow-1 m-0">
                        {{ title }}
                    </h5>

                    <!-- Right: Close button -->
                    <button
                        type="button"
                        class="btn-close"
                        @click="closeModal()"
                    ></button>
                </div>

                <div v-if="loading" class="loading-container">
                    <span class="spinner"></span>
                </div>

                <div v-else class="modal-body">
                    <slot name="body">Default body content</slot>
                </div>
                <!-- always render the wrapper, but only show if parent provided a `footer` slot -->
                <div class="modal-footer" v-if="$slots.footer">
                    <slot name="footer" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    registerModal,
    unregisterModal,
    syncBackdropState,
} from "@/composables/useModalBackdrop";
import { nextTick } from "vue";

export default {
    name: "BootstrapModal",
    props: {
        modalId: { type: String, required: true },
        title: { type: String, default: "Modal title" },
        closeText: { type: String, default: "Close" },
        saveText: { type: String, default: "Save changes" },
        modalSize: {
            type: String,
            default: "lg",
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            modalShow: false,
            modalInstance: null,
            modalFooter: false,
            isClosing: false,
        };
    },
    emits: ["save", "close", "open"],
    methods: {
        // Open the modal. If no id is passed, fall back to this.modalId
        showModal(id) {
            const targetId = id || this.modalId;
            if (!targetId) {
                console.error("Modal ID is required to open the modal.");
                return;
            }

            this.$emit("open");
            this.modalShow = true;
            registerModal();

            nextTick(() => {
                const modalElement = document.getElementById(targetId);

                if (!modalElement) {
                    console.error(`Modal with ID ${targetId} not found.`);
                    return;
                }

                if (!this.modalInstance) {
                    this.modalInstance = new bootstrap.Modal(modalElement, {
                        backdrop: false,
                        keyboard: true,
                    });
                }

                modalElement.classList.add("show");
                modalElement.style.display = "block";
                this.modalInstance.show();
            });
        },
        // Close the modal. If no id is passed, fall back to this.modalId
        closeModal(id) {
            // Guard against recursive close calls (e.g., parent @close handler calling back into closeModal)
            if (this.isClosing) return;
            this.isClosing = true;

            const targetId = id || this.modalId;
            if (!targetId) {
                console.error("Modal ID is required to close the modal.");
                this.isClosing = false;
                return;
            }

            const modalElement = document.getElementById(targetId);

            // Dispose Bootstrap instance first while element still exists
            if (this.modalInstance) {
                try {
                    this.modalInstance.dispose();
                } catch (e) {
                    // Ignore disposal errors
                }
                this.modalInstance = null;
            }

            // Update DOM/state
            if (modalElement) {
                modalElement.classList.remove("show");
                modalElement.style.display = "none";
            }

            this.modalShow = false;

            // Properly unregister so the shared backdrop count stays in sync
            nextTick(() => {
                unregisterModal();
            });

            // Emit the close event once; guard prevents re-entrancy
            this.$emit("close");

            // release the guard after emit completes
            this.isClosing = false;
        },
    },
};
</script>

<style scoped>
.modal-content {
    border-radius: 8px;
}
.modal-header {
    border-bottom: 1px solid #dee2e6;
}
.modal-footer {
    border-top: 1px solid #dee2e6;
}
.loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    height: 100px;
}

/* Spinner */
.spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-top: 4px solid var(--primary); /* Same green color */
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
